import './LacksInput.css';

import { useState } from 'react';
import {
	TextField,
	Autocomplete,
	createFilterOptions,
	Button,
	FormControl,
	FormLabel,
	ToggleButton,
	ToggleButtonGroup,
	MenuItem,
	Stack,
	InputAdornment,
	Dialog,
	DialogActions,
	DialogContent,
} from '@mui/material';
import {
	Delete,
	Edit,
	InfoOutlined,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { FormDialog } from '../FormDialog/FormDialog.jsx';

const filter = createFilterOptions();

export function LackEntry({ lack, options, onChangeLack, onDeleteLack, onCreateLack }) {
	const { t } = useSelector(redux => redux.store);
	const [isOpen, setIsOpen] = useState(false);

	/** @todo Perhaps cast to string -> String(...) === String(...) to be sure? */
	lack.selectedLack = options.find((option) => Boolean(option.id) && option.id === lack.selected_lack_id);

	function getFullTitle(option) {
		if (typeof option === 'string') {
			return option;
		} else {
			const title = option.title || option.selectedLack?.title || '';
			const code = option.code || option.selectedLack?.code || '';
			return code
				? `${code}: ${title}`
				: title;
		}
	}

	return (
		<div className={`lacks-input__lack-entry ${ lack.delete_me && 'lacks-input__lack-entry--deleted' }`}>
			<Autocomplete
				freeSolo
				selectOnFocus
				clearOnBlur
				disableClearable
				disabled={ lack.delete_me }
				options={ options }
				value={ lack.title || lack.selectedLack?.title || '' }
				getOptionLabel={ (option) => getFullTitle(option) }
				onChange={(_, newValue) => {
					if (newValue.inputValue) { // typed a thing instead of clicked
						onCreateLack(newValue.inputValue);

						/** @todo this explicitly resets properties if they were
						 * set, but more elegant would be to create a new object
						 * and explicitly keep a couple of properties instead
						 * of the reverse */
						lack.selected_lack_id = null;
						lack.title = newValue.inputValue;
						lack.importance = null;
						lack.intensity = null;
						lack.extent = null;
						lack.selectedLack = {};
					} else {
						/** @todo there's a reason we don’t simply copy all
						 * newValue's properties to a new lack, but elegant,
						 * this is not */
						onChangeLack({
							id: lack.id,
							description: lack.description,
							cost: lack.cost,
							selected_lack_id: newValue.id,
							title: newValue.title,
							importance: newValue.importance,
							intensity: newValue.intensity,
							extent: newValue.extent,
							selectedLack: newValue,
						});
					}
				}}
				filterOptions={(options, state) => {
					// createFilterOptions compares state.newValue with the
					// title property of the passed options. Luckily, our options
					// array contains objects with a title property, so this
					// magic below just works
					const filtered = filter(options, state);
					const { inputValue } = state;

					const isExisting = options.some((option) => inputValue === option.title);
					if (inputValue !== '' && !isExisting) {
						filtered.push({
							inputValue,
							title: `${t.action_add} "${inputValue}"`,
						});
					}

					return filtered;
				}}
				renderInput={(params) => (
					<>
						<TextField
							{...params}
							label={t.nen_lacks_title || 't.nen_lacks_title'}
						/>
					</>
				)}
			/>
			<Button
				variant='outlined'
				onClick={() => setIsOpen(true)}
				disabled={ lack.delete_me || (!lack.title && !lack.selectedLack?.title) }
			>
				<Edit/>
			</Button>
			<Button
				disabled={ lack.delete_me }
				variant='outlined'
				color="error"
				onClick={() => confirm(t.lack_delete_confirm || 't.lack_delete_confirm') && onDeleteLack(lack)}
			>
				<Delete/>
			</Button>

			<LackDetailsDialog
				lack={lack}
				onChangeLack={onChangeLack}
				isOpen={isOpen}
				setIsOpen={setIsOpen}
			/>
		</div>
	);
}

function LackDetailsDialog({ lack, onChangeLack, isOpen, setIsOpen }) {
	const { t } = useSelector(redux => redux.store);

	return (
		<FormDialog
			title={ getTitle() }
			isOpen={isOpen}
			closeHandler={() => setIsOpen(false)}
			canBeCanceled={false}
			primaryAction={{
				handler: () => {
					onChangeLack(lack);
					setIsOpen(false);
				},
				label: t.save,
			}}
		>
			<LackDetails
				lack={lack}
				onChangeLack={onChangeLack}
			/>
		</FormDialog>
	);

	/**
	 * Get title for form dialog, in- or excluding code
	 * @return {String}
	 */
	function getTitle() {
		return lack.code || lack.selectedLack?.code
			? `${lack.code || lack.selectedLack?.code}: ${lack.title || lack.selectedLack?.title}`
			: lack.title || lack.selectedLack?.title;
	}
}

/**
 * Renders a lack details form, either to be used on LacksOverview or in a modal
 * with the inspection part.
 *
 * @note A hidden input is rendered when validation is turned on, but the name/
 * value of these fields are not used to store the lack on the server. They are
 * stored with the inspectionData object that is posted to the BFF, however.
 * This might be confusing, but so is everything else here
 *
 * @param {Object} options.lack
 * @param {Function}  options.onChangeLack
 * @param {Array} options.errorElements Passed via Inspection.js
 * @param {string} options.id If validate is true, will be used to identify the input[type=hidden] that is used for form validation
 * @param {Boolean} options.validate Triggers rendering an input[type=hidden] that is picked up by the validation routine
 */
export function LackDetails({ lack, onChangeLack, errorElements = [], id = null, validate = false }) {
	const { t } = useSelector(redux => redux.store);
	const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);


	// react-dom.development.js:67 Warning: `value` prop on `input` should not be null. Consider using an empty string to clear the component or `undefined` for uncontrolled components.

	const fields = {
		importance: {
			id: `${id}_importance`,
			isDisabled: Boolean(lack.selectedLack?.condition?.importance) || lack.delete_me,
			options: [
				{value: 'high', title: t.lack_importance_high || 't.lack_importance_high', color: 'error' },
				{value: 'medium', title: t.lack_importance_medium || 't.lack_importance_medium', color: 'warning' },
				{value: 'low', title: t.lack_importance_low || 't.lack_importance_low', color: 'success' },
			],
		},
		intensity: {
			id: `${id}_intensity`,
			isDisabled: Boolean(lack.selectedLack?.condition?.intensity) || lack.delete_me,
			options: [
				{value: 'start', title: t.lack_intensity_start || 't.lack_intensity_start', color: 'success' },
				{value: 'middle', title: t.lack_intensity_middle || 't.lack_intensity_middle', color: 'warning' },
				{value: 'end', title: t.lack_intensity_end || 't.lack_intensity_end', color: 'error' },
			],
		},
		extent: {
			id: `${id}_extent`,
			isDisabled: Boolean(lack.selectedLack?.condition?.extent) || lack.delete_me,
			options: [
				{value: 'lowest', title: t.lack_extent_lowest || 't.lack_extent_lowest'},
				{value: 'low', title:  t.lack_extent_low || 't.lack_extent_low'},
				{value: 'medium', title:  t.lack_extent_medium || 't.lack_extent_medium'},
				{value: 'high', title:  t.lack_extent_high || 't.lack_extent_high'},
				{value: 'highest', title:  t.lack_extent_highest || 't.lack_extent_highest'},
			],
		},
	};

	return (
		<Stack
			className="lack-details"
			spacing={4}
		>
			<Stack direction="row" spacing={4}>
				<FormControl
					id={fields.importance.id}
					variant="standard"
					disabled={fields.importance.isDisabled}
				>
					<FormLabel>
						{t.nen_importance || 't.nen_importance'}
					</FormLabel>
					{ validate && !fields.importance.isDisabled &&
						<input
							type="hidden"
							name={fields.importance.id}
							value={lack.importance || undefined}
						/>
					}
					<ToggleButtonGroup
						size="small"
						value={lack.selectedLack?.condition?.importance || lack.importance}
						exclusive
						onChange={(event, importance) => onChangeLack({...lack, importance})}
						disabled={fields.importance.isDisabled}
						className={errorElements.includes(fields.importance.id) ? 'has-error' : ''}
					>
						{fields.importance.options.map(({ value, title, color }) => (
							<ToggleButton key={value} value={value} color={ color }>{title}</ToggleButton>
						))}
					</ToggleButtonGroup>
				</FormControl>
				<FormControl
					id={fields.intensity.id}
					disabled={fields.intensity.isDisabled}
				>
					<FormLabel>
						{t.nen_intensity || 't.nen_intensity'}
					</FormLabel>
					{ validate && !fields.intensity.isDisabled &&
						<input
							type="hidden"
							name={fields.intensity.id}
							value={lack.intensity || undefined}
						/>
					}
					<ToggleButtonGroup
						size="small"
						value={lack.selectedLack?.condition?.intensity || lack.intensity}
						exclusive
						onChange={(event, intensity) => onChangeLack({...lack, intensity})}
						disabled={fields.intensity.isDisabled}
						className={errorElements.includes(fields.intensity.id) ? 'has-error' : ''}
					>
						{fields.intensity.options.map(({ value, title, color }) => (
							<ToggleButton key={value} value={value} color={color}>{title}</ToggleButton>
						))}
					</ToggleButtonGroup>
				</FormControl>
				<FormControl
					id={fields.extent.id}
					disabled={fields.extent.isDisabled}
				>
					<FormLabel>
						{t.nen_extent || 't.nen_extent'}
					</FormLabel>
					{ validate && !fields.extent.isDisabled &&
						<input
							type="hidden"
							name={fields.extent.id}
							value={lack.extent || undefined}
						/>
					}
					<TextField
						select
						size="small"
						value={lack.selectedLack?.condition?.extent || lack.extent || ''}
						onChange={(event) => onChangeLack({...lack, extent: event.target.value})}
						disabled={fields.extent.isDisabled}
						className={errorElements.includes(fields.extent.id) ? 'has-error' : ''}
					>
						{fields.extent.options.map(({ value, title }) => (
							<MenuItem key={value} value={value}>{title}</MenuItem>
						))}
					</TextField>
				</FormControl>
			</Stack>
			<Stack direction="row" spacing={4}>
				<Button
					variant="text"
					className="lack-details__more-info"
					endIcon={<InfoOutlined /> }
					onClick={() => { setIsMoreInfoOpen(true); }}
				>
					{t.nen_more_info || 't.nen_more_info'}
				</Button>
				<Dialog
					open={isMoreInfoOpen}
					onClose={() => setIsMoreInfoOpen(false)}
				>
					<DialogActions>
						<Button onClick={() => setIsMoreInfoOpen(false)}>
							{t.close}
						</Button>
					</DialogActions>
					<DialogContent
						className="lack-details__more-info-popup">
						<h1>{ t.nen_more_info_header || 't.nen_more_info_header' }</h1>
						<p>{ t.nen_more_info_subtitle || 't.nen_more_info_subtitle' }</p>
						<h2>{ t.nen_more_info_importance_title || 't.nen_more_info_importance_title' }</h2>
						<p>{ t.nen_more_info_importance_text || 't.nen_more_info_importance_text' }</p>
						<h2>{ t.nen_more_info_intensity_title || 't.nen_more_info_intensity_title' }</h2>
						<p>{ t.nen_more_info_intensity_text || 't.nen_more_info_intensity_text' }</p>
						<h2>{ t.nen_more_info_extent_title || 't.nen_more_info_extent_title' }</h2>
						<p>{ t.nen_more_info_extent_text || 't.nen_more_info_extent_text' }</p>
					</DialogContent>
				</Dialog>

			</Stack>
			<Stack direction="row" spacing={4}>
				<FormControl>
					<FormLabel>{ t.nen_costs || 't.nen_costs' }</FormLabel>
					<TextField
						type="number"
						disabled={ lack.delete_me }
						value={Number(lack.cost) || ''}
						onChange={(event) => onChangeLack({...lack, cost: event.target.value})}
						InputProps={{
							startAdornment: <InputAdornment position="start">€</InputAdornment>,
						}}

					/>
				</FormControl>
				<FormControl fullWidth>
					<FormLabel>{ t.nen_description || 't.nen_description' }</FormLabel>
					<TextField
						multiline
						disabled={ lack.delete_me }
						value={lack.description || ''}
						maxRows={5}
						onChange={(event) => onChangeLack({...lack, description: event.target.value})}
					/>
				</FormControl>
			</Stack>
		</Stack>
	);
}
